import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Map } from 'immutable';
import { NEVER, Observable } from 'rxjs';

import { State } from '®app/ngrx';
import { AppConfig } from '®shared/config';
import {
  CONTAINER_MODE,
  CONTAINER_ROOT_TYPE,
  ContainedTypeAndId,
  ContainerInitialisedAction,
  ContainerModes,
  ContainerStructure,
  ContainerType,
  LIST_ID,
  getContainedTypeAndIdOnly,
  selectContainerStructure,
  selectSectionsById,
} from '®shared/container-structure';
import {
  CurrentUserAndPermissions,
  CurrentUserFactoryProvider,
} from '®shared/current-user';
import { DashboardAnalyticsService } from '®shared/dashboard-analytics';
import { EventsService } from '®shared/events';
import {
  LAUNCH_METHOD,
  LaunchMethod,
  LaunchMethodFactoryProvider,
} from '®shared/events/launch-method.constant';
import {
  LAUNCH_MODE,
  LaunchMode,
  getValidatedLaunchMode,
} from '®shared/events/launch-mode.constant';
import { GA_PAGE_NAMES } from '®shared/google-analytics';
import { GoogleAnalyticsGA4Service } from '®shared/google-analytics/google-analytics-ga4';
import { selectFilteredEntryIds } from '®shared/list-item/store';
import { UserPermissions } from '®shared/permissions';
import { createListEntryPointEvent } from '®shared/reading-list/events';
import {
  EditableSection,
  ReadingListDetails,
  Section,
} from '®shared/reading-list/models';
import { InitialiseWithTenantAction } from '®shared/tenant';
import { UriService } from '®shared/url-utils';
import { FetchUserAnnotationsForListAction } from '®shared/user-annotations';

import { LinkTypeIdentifier } from '../../lti/shared/link-type-identifier';

export type SectionRouteData = {
  currentUser: CurrentUserAndPermissions;
  section: Section;
  isDraft: boolean;
};

export function getListIdFromSection(route: ActivatedRoute): string {
  return route.snapshot.data.section.list.id;
}

export function getLaunchModeFromRoute(route: ActivatedRoute): LaunchMode {
  const { data } = route.snapshot;
  const { isDraft = false } = data;
  return getValidatedLaunchMode(ContainerModes.View, isDraft);
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rl-section-view',
  templateUrl: './section-view.component.html',
  providers: [
    { provide: CONTAINER_MODE, useValue: ContainerModes.View },
    { provide: CONTAINER_ROOT_TYPE, useValue: ContainerType.Section },
    CurrentUserFactoryProvider,
    LaunchMethodFactoryProvider,
    {
      provide: LAUNCH_MODE,
      useFactory: getLaunchModeFromRoute,
      deps: [ActivatedRoute],
    },
    {
      provide: LIST_ID,
      useFactory: getListIdFromSection,
      deps: [ActivatedRoute],
    },
  ],
})
export class SectionViewComponent implements OnInit {
  containerRoot: ContainedTypeAndId;
  containerStructure$: Observable<ContainerStructure>;
  filteredEntryIds$: Observable<Map<string, boolean>>;
  sectionsById$: Observable<Map<string, EditableSection>>;
  forceRerender$ = NEVER;
  list: ReadingListDetails;
  rolledOverTo: Section | null;
  rolledOverFrom: Section | null;
  listUrl: string;
  listTitle: string;
  isDraft: boolean;
  queryParamMap: ParamMap;
  permissions: UserPermissions;

  private canAccessDraftLists: boolean;

  constructor(
    private appConfig: AppConfig,
    private dashboardAnalyticsService: DashboardAnalyticsService,
    private eventsService: EventsService,
    private googleAnalyticsGA4: GoogleAnalyticsGA4Service,
    @Inject(LAUNCH_METHOD) private launchMethod: LaunchMethod,
    @Inject(LAUNCH_MODE) private launchMode: LaunchMode,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<State>,
    private titleService: Title,
    private uriService: UriService,
  ) {
    this.containerStructure$ = this.store.select(selectContainerStructure);
    this.sectionsById$ = this.store.select(selectSectionsById);
    this.filteredEntryIds$ = this.store.select(selectFilteredEntryIds);
  }

  ngOnInit() {
    this.route.data.forEach(this.extractDataFromActivatedRoute.bind(this));
  }

  /**
   * Extracts data from the ActivatedRoute and sets up the component
   *
   * @param {SectionRouteData} data - resolved section view dependencies
   */
  private extractDataFromActivatedRoute(data: SectionRouteData): void {
    // main data is retrieved from route resolvers
    const { currentUser, section, isDraft } = data;

    this.list = section.list;
    this.rolledOverTo = section.rolled_over_to;
    this.rolledOverFrom = section.rolled_over_from;
    const tenant = section.tenant;
    this.store.dispatch(new InitialiseWithTenantAction(tenant));

    const listId = this.list.id;
    this.containerRoot = getContainedTypeAndIdOnly(section);

    this.store.dispatch(new ContainerInitialisedAction({ container: section }));

    this.listTitle = this.list.title;
    this.listUrl = this.uriService.getListViewUrl(listId);
    this.isDraft = isDraft;

    this.permissions = currentUser.permissions;
    this.canAccessDraftLists = currentUser.canAccessDraftLists;

    // user-centric content
    if (currentUser.hasTarlAccount) {
      // load the annotations for the requested section's list
      this.store.dispatch(
        new FetchUserAnnotationsForListAction({
          listId,
          userId: currentUser.oauthUser.guid,
        }),
      );
    }

    this.setWindowTitle(section.title, tenant.name);

    const currentUrl = this.uriService.getTarlSectionViewUrl(
      section.id,
      isDraft,
    );

    const isLoggedIn = !currentUser.oauthUser.isAnonymous;
    this.googleAnalyticsGA4.sendFakePageView(
      currentUrl,
      GA_PAGE_NAMES.SECTION_VIEW,
      isLoggedIn,
    );

    this.dashboardAnalyticsService.logListOutlineView(listId);
    this.queryParamMap = this.route.snapshot.queryParamMap;

    this.eventsService.recordEvent(
      createListEntryPointEvent(
        listId,
        { type: 'sections', id: section.id },
        this.launchMethod,
        this.launchMode,
      ),
    );
  }

  setWindowTitle(newTitle: string, tenantName: string) {
    this.titleService.setTitle(`${newTitle} | ${tenantName}`);
  }

  isNotLtiEmbedded() {
    return !this.appConfig.isAnLti1p0EmbeddedSession;
  }

  isLti1p3DeepLink(): boolean {
    const lti1p3LinkType = this.appConfig?.session?.lti1p3LaunchType;
    return lti1p3LinkType === LinkTypeIdentifier.deepLink;
  }

  showDeepLinkRelinkingButton(): boolean {
    if (!this.canAccessDraftLists) {
      return false;
    }

    const isInRolloverChain =
      this.rolledOverFrom != null || this.rolledOverTo != null;
    const hasLaunchId = this.route.snapshot.queryParamMap.has('lti1p3LaunchId');
    return this.isLti1p3DeepLink() && isInRolloverChain && hasLaunchId;
  }

  navigateToDeepLinkRelinking() {
    const paramMap = this.route.snapshot.paramMap;
    const version = paramMap.get('version');
    const tenantCode = paramMap.get('tenantCode');
    const sectionId = paramMap.get('sectionId').split('.').shift();

    const launchId = this.route.snapshot.queryParamMap.get('lti1p3LaunchId');

    this.router.navigate(
      [version, tenantCode, 'deep_link_relinking', 'sections', sectionId],
      {
        queryParams: {
          lti1p3LaunchId: launchId,
        },
      },
    );
  }
}
